import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Combines and merges CSS class names with Tailwind CSS optimization.
 *
 * This utility function serves two main purposes:
 * 1. **Conditional class merging**: Uses clsx to conditionally join class names, filtering out falsy values
 * 2. **Tailwind CSS optimization**: Uses tailwind-merge to properly merge conflicting Tailwind classes
 *
 * @param inputs - Variable number of class values which can be:
 *   - Strings: "text-red-500", "bg-blue-100"
 *   - Conditional objects: { "hidden": isVisible }
 *   - Arrays: ["text-sm", "font-medium"]
 *   - Falsy values: null, undefined, false (these are filtered out)
 *
 * @returns A single string of merged and optimized class names
 *
 * @example
 * ```typescript
 * // Basic usage
 * cn("text-red-500", "bg-blue-100") // "text-red-500 bg-blue-100"
 *
 * // Conditional classes
 * cn("text-sm", { "font-bold": isActive }) // "text-sm" or "text-sm font-bold"
 *
 * // Tailwind CSS conflict resolution
 * cn("text-red-500", "text-blue-500") // "text-blue-500" (last one wins)
 *
 * // Mixed usage
 * cn("flex items-center", isLarge && "text-lg", !isVisible && "hidden")
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
